import { HTTPClientInterface, HTTPRequest } from "@shared/core/HTTPClientInterface";

import { buildURL, SystemURL } from "./URLCatalog";

/** What the adoption column shows for a collaborator. Mirrors the backend enum. */
export type CollaboratorAdoptionStatus =
    | "INSTALLED"
    | "INVITED"
    | "NOT_INSTALLED"
    | "NO_WHATSAPP";

/** One active collaborator with their Qontact Auth installation state. */
export interface CollaboratorAdoptionEntry {
    userID: string;
    name?: string;
    imageURL?: string;
    email?: string;
    mobilePhone?: string;
    /** The company/branch the employer filed the collaborator under ("Empresa"). */
    empresa?: string;
    adoptionStatus: CollaboratorAdoptionStatus;
    devicePlatform?: string;
    appVersion?: string;
    /** From the installation's appMetadata, as reported by the app (e.g. "Samsung Galaxy A54"). */
    deviceModel?: string;
    /** From the installation's appMetadata, as reported by the app (e.g. "14"). */
    osVersion?: string;
    installedAt?: number;
    /** Device rows: last app activity. Invited rows: last (re)send. Otherwise absent. */
    lastActivityAt?: number;
    invitationSentAt?: number;
    invitationExpiresAt?: number;
}

export interface AppAdoptionInvitationSummary {
    userID: string;
    status: "ENQUEUED" | "SKIPPED_ALREADY_INSTALLED";
    pendingMessageID?: string;
    expiresAt?: number;
}

export interface AppAdoptionInvitationFailure {
    userID: string;
    error: string;
}

export interface AppAdoptionInvitationsResult {
    enqueued: AppAdoptionInvitationSummary[];
    skipped: AppAdoptionInvitationSummary[];
    failed: AppAdoptionInvitationFailure[];
}

export class AppAdoptionService {
    private httpClient: HTTPClientInterface;

    constructor(httpClient: HTTPClientInterface) {
        this.httpClient = httpClient;
    }

    async getCollaboratorAdoption(organizationID: string): Promise<CollaboratorAdoptionEntry[]> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.AuthAppInstall.getCollaborators, {
            organization_id: organizationID,
        });
        request.method = "GET";
        const response = await this.httpClient.execute(request);
        return (response.result ?? []) as CollaboratorAdoptionEntry[];
    }

    async sendInvitations(
        organizationID: string,
        userIDs: string[],
        options: { reinstall?: boolean } = {}
    ): Promise<AppAdoptionInvitationsResult> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.AuthAppInstall.postInvitations, {});
        request.method = "POST";
        // reinstall: re-onboarding for collaborators whose installation record is
        // stale (manual uninstall) — the backend skips them otherwise.
        request.body = options.reinstall
            ? { organizationID, userIDs, reinstall: true }
            : { organizationID, userIDs };
        const response = await this.httpClient.execute(request);
        const result = (response.result ?? {}) as Partial<AppAdoptionInvitationsResult>;
        return {
            enqueued: result.enqueued ?? [],
            skipped: result.skipped ?? [],
            failed: result.failed ?? [],
        };
    }
}
